import React, { memo, useEffect } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
// components
import { Box, debounce, TextField } from '@mui/material';
import Text from 'components/text';
import AutocompleteInputController from 'controllers/autocomplete-input-controller';
import CheckboxController from 'controllers/checkbox-controller';
import { useCallback, useState } from 'react';
import api from 'utils/api';
import { ISpotifyData } from 'utils/apiTypes';
import { IArtistOption, spotifyArtistsToOptions } from 'utils/helpers';
import { getSpotifyArtistErrorMessage } from 'utils/spotifyErrors';
import { useError } from 'hooks/useError';
import ErrorModal from 'components/error-modal';
import BaseInputController from './base-input-controller';
import { WithTranslation, withTranslation } from 'react-i18next';

interface OwnProps {
  spotifyIdName?: string;
  artistFieldName?: string;
  youtubeIdName?: string;
  withError?: boolean;
  [x: string]: any;
}

const ArtistInputController: React.FC<OwnProps & WithTranslation> = ({
  t,
  i18n,
  spotifyIdName = 'spotifyId',
  youtubeIdName = 'youtubeId',
  artistFieldName = 'artistName',
  withError = false,
  ...props
}) => {
  const [artistList, setArtistList] = useState<IArtistOption[]>([]);
  const { message, setError } = useError();
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const { control, watch, setValue } = useFormContext();

  const watchNoSpotifyId = watch('noSpotifyId', false);

  useEffect(() => {
    if (watchNoSpotifyId) {
      setValue(spotifyIdName, null);
      setValue(artistFieldName, null);
    } else {
      setValue(youtubeIdName, null);
      setValue(artistFieldName, null);
    }
  }, [watchNoSpotifyId]);

  useEffect(() => {
    const subscription = watch((value, { name: fieldName, type }) => {
      if (fieldName === spotifyIdName) {
        const artistName: string | undefined = artistList.find(
          (artist) => artist.value === value.spotifyId
        )?.label;
        setValue(artistFieldName, artistName);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, artistList, spotifyIdName, setValue, artistFieldName]);

  const searchArtist = useCallback(
    async (value: string) => {
      if (value === '') return;
      try {
        const data: ISpotifyData = await api.spotify.getSpotifyArtist({ id: value });
        const artists = spotifyArtistsToOptions(data);
        setArtistList(artists);
      } catch (err) {
        setError(getSpotifyArtistErrorMessage(err));
        setOpenErrorModal(true);
      }
    },
    [setError]
  );

  const handleSearchArtist = debounce(searchArtist, 300);

  return (
    <>
      {!watchNoSpotifyId && (
        <>
          <AutocompleteInputController
            options={artistList}
            name={spotifyIdName}
            withError={withError}
            handleChange={handleSearchArtist}
            label={t('Search Artist or enter Spotify ID')}
            control={control}
            {...props}
          />
        </>
      )}
      {watchNoSpotifyId && (
        <BaseInputController
          withError
          name={youtubeIdName}
          type="text"
          variant="filled"
          label={t('YouTube URL or ID')}
        />
      )}
      <BaseInputController
        withError
        name={artistFieldName}
        type="text"
        label={t('Artist Name')}
        sx={{ ...(watchNoSpotifyId ? {} : { display: 'none' }) }}
      />

      <Box display="flex" alignItems="center" paddingBottom={2} component="label">
        <CheckboxController name="noSpotifyId" />
        <Text fontSize={17}>{t('No Spotify ID?')}</Text>
      </Box>
      <ErrorModal
        open={openErrorModal}
        handleClose={() => setOpenErrorModal(false)}
        message={message}
      />
    </>
  );
};

export default withTranslation()(ArtistInputController);
